/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
// src/services/AuthService.ts
import { ApiBack } from '../axios-config';
import { AES } from 'crypto-es/lib/aes';
import { Utf8 } from 'crypto-es/lib/core';
import { secret } from '../secret';

interface JwtPayload {
	fresh: boolean;
	iat: number;
	jti: string;
	type: string;
	sub: string;
	nbf: number;
	csrf: string;
	exp: number;
}

const SESSION_STORAGE_KEY_ACCESS_TOKEN = 'access_token';
const LOCAL_STORAGE_KEY_REFRESH_TOKEN = 'refresh_token';
const LOCAL_STORAGE_USER_TOKEN = 'user_token';

async function login(login: string, senha: string): Promise<boolean | Error> {
	try {
		const { data } = await ApiBack.post('/login', { login, senha });

		if (data) {
			let access_token = data.access_token;
			let refresh_token = data.refresh_token;
			let user_token_raw = data.user; 

			user_token_raw = JSON.stringify(user_token_raw);

			access_token = AES.encrypt(access_token, secret).toString();
			refresh_token = AES.encrypt(refresh_token, secret).toString();
			let user_token_encrypted = AES.encrypt(user_token_raw, secret).toString();

			localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, user_token_encrypted); 
			sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, access_token);
			localStorage.setItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN, refresh_token);

			return true;
		}

		return new Error('Erro no login.');
	} catch (error) {
		return new Error(
			(error as { message: string }).message || 'Erro no login.'
		);
	}
}

async function loginWithMicrosoft(): Promise<void | Error> {
	try {
		const backendUrl = ApiBack.defaults.baseURL || 'http://localhost:5000';
		sessionStorage.setItem('pre_microsoft_login_url', window.location.href);
		window.location.href = `${backendUrl}/login-ms`;
		return;
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
				'Erro ao iniciar login com Microsoft.'
		);
	}
}

async function processMicrosoftCallback(
	code: string
): Promise<boolean | Error> {
	try {
		const response = await fetch(
			`${ApiBack.defaults.baseURL}/getAToken?code=${code}`,
			{
				method: 'GET',
				credentials: 'include',
			}
		);

		if (response.ok) {
			const token = await response.text();

			if (token && token.trim()) {
				const access_token = AES.encrypt(token.trim(), secret).toString();
				sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, access_token);

				try {
					const payload = getPayload(token.trim());
					const basicUser = {
						id: payload.sub,
						uuid: payload.sub,
					};

					const encrypted_user_token = AES.encrypt(
						JSON.stringify(basicUser),
						secret
					).toString();
					localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, encrypted_user_token);
				} catch (payloadError) {
					console.warn(
						'Não foi possível extrair dados do usuário do token:',
						payloadError
					);
				}

				return true;
			}
		}

		try {
			const data = await response.json();
			if (data.access_token) {
				const access_token = AES.encrypt(data.access_token, secret).toString();
				sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, access_token);

				if (data.user) {
					const user_token = AES.encrypt(
						JSON.stringify(data.user),
						secret
					).toString();
					localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, user_token);
				}

				if (data.refresh_token) {
					const refresh_token = AES.encrypt(
						data.refresh_token,
						secret
					).toString();
					localStorage.setItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN, refresh_token);
				}

				return true;
			}
		} catch (jsonError) {
			console.warn('Resposta não é JSON válido:', jsonError);
		}

		return new Error('Token não recebido do backend');
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
				'Erro ao processar callback do Microsoft'
		);
	}
}

async function processMicrosoftToken(token: string): Promise<boolean | Error> {
	try {
		if (token && token.trim()) {
			const cleanToken = token.trim().replace(/['"]/g, '');

			try {
				const payload = getPayload(cleanToken);

				const exp = new Date(payload.exp * 1000);
				const now = new Date();
				if (exp < now) {
					return new Error('Token expirado');
				}
			} catch (jwtError) {
				return new Error('Token inválido');
			}

			const tokenWithBearer = `Bearer ${cleanToken}`;
			const access_token = AES.encrypt(tokenWithBearer, secret).toString();
			sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, access_token);

			const urlParams = new URLSearchParams(window.location.search);
			const userData = urlParams.get('user_data');

			if (userData) {
				try {
					const decodedUserData = JSON.parse(atob(userData));

					const user_token = AES.encrypt(
						JSON.stringify(decodedUserData),
						secret
					).toString();
					localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, user_token);
					return true;
				} catch (decodeError) {
					return new Error('Erro ao processar dados do usuário');
				}
			} else {
				const payload = getPayload(cleanToken);
				const basicUser = {
					id: payload.sub,
					uuid: payload.sub,
					sub: payload.sub,
				};

				const encrypted_user_token = AES.encrypt(
					JSON.stringify(basicUser),
					secret
				).toString();
				localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, encrypted_user_token);

				return new Error(
					'Dados do usuário incompletos. Backend deve incluir user_data na URL.'
				);
			}
		}

		return new Error('Token inválido');
	} catch (error) {
		return new Error('Erro ao processar token Microsoft');
	}
}

async function handleMicrosoftCallback(): Promise<boolean | Error> {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		const token = urlParams.get('token');
		const refresh_token = urlParams.get('refresh_token');
		const user_encoded = urlParams.get('user');
		const error = urlParams.get('error');

		if (error) {
			return new Error(`Erro no login Microsoft: ${error}`);
		}

		if (token) {
			const encrypted_access_token = AES.encrypt(token, secret).toString();
			sessionStorage.setItem(
				SESSION_STORAGE_KEY_ACCESS_TOKEN,
				encrypted_access_token
			);

			if (refresh_token) {
				const decoded_refresh = atob(refresh_token);
				const encrypted_refresh_token = AES.encrypt(
					decoded_refresh,
					secret
				).toString();
				localStorage.setItem(
					LOCAL_STORAGE_KEY_REFRESH_TOKEN,
					encrypted_refresh_token
				);
			}

			if (user_encoded) {
				const user_json = atob(user_encoded);
				const encrypted_user_token = AES.encrypt(user_json, secret).toString();
				localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, encrypted_user_token);
			}

			window.history.replaceState({}, document.title, window.location.pathname);

			return true;
		}

		return new Error('Token não encontrado');
	} catch (error) {
		return new Error('Erro ao processar callback do Microsoft Login');
	}
}

async function logout(token: string): Promise<string | any> {
	const tokenDescriptografado = AES.decrypt(token, secret).toString(Utf8);

	try {
		const { data } = await ApiBack.post('/logout', null, {
			headers: {
				Authorization: `${tokenDescriptografado}`,
			},
		});

		if (data) {
			return data;
		}
		return new Error('Erro no logout.');
	} catch (error) {
		return new Error(
			(error as { message: string }).message || 'Erro no login.'
		);
	}
}

async function refresh(): Promise<boolean | Error> {
	const token = localStorage.getItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN);

	if (!token) {
		return new Error('Token de refresh não encontrado.');
	}

	try {
		const tokenDescriptografado = AES.decrypt(token, secret).toString(Utf8);

		try {
			const { data } = await ApiBack.post('/refresh', null, {
				headers: {
					Authorization: `${tokenDescriptografado}`,
				},
			});
			const novoToken = AES.encrypt(data.access_token, secret).toString();
			sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, novoToken);

			return true;
		} catch (error) {
			return false;
		}
	} catch (error) {
		return false;
	}
}

async function decrypt(token: string): Promise<string | Error> {
	try {
		const tokenDescriptografado = AES.decrypt(token, secret).toString(Utf8);
		return tokenDescriptografado;
	} catch (error) {
		return new Error(
			(error as { message: string }).message || 'Erro na descriptografia.'
		);
	}
}

function encrypt(data: string) {
	const dataEncrypt: string = AES.encrypt(data, secret).toString();
	return dataEncrypt;
}

async function getAccessToken(): Promise<string | Error> {
	const sessionToken = sessionStorage.getItem('access_token');

	if (sessionToken !== null) {
		const token = await AuthService.decrypt(sessionToken);
		return token;
	} else {
		return new Error('Erro getAccessToken.');
	}
}

async function getRefreshToken(): Promise<string | Error> {
	const localToken = sessionStorage.getItem('refresh_token');

	if (localToken !== null) {
		const token = await AuthService.decrypt(localToken);
		return token;
	} else {
		return new Error('Erro getRefreshToken.');
	}
}

async function removeAccess() {
	localStorage.removeItem('refresh_token');
	sessionStorage.removeItem('access_token');
	localStorage.removeItem('user_token');
}

function getPayload(jwt: string): JwtPayload {
	const base64Url = jwt.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join('')
	);

	return JSON.parse(jsonPayload);
}

async function verify() {
	const Acesstoken = await getAccessToken();

	if (!(Acesstoken instanceof Error)) {
		const payload = getPayload(Acesstoken);
		const exp = new Date(payload.exp * 1000);
		const now = new Date();
		const margin = 300000;

		if (exp.getTime() - now.getTime() < margin) {
			const result = await refresh();
			if (result instanceof Error || result === false) {
				removeAccess();
				return false;
			}
			return true;
		} else {
			return true;
		}
	}
	return false;
}

async function forgot_password(email: string) {
	try {
		await ApiBack.post('/forgot_password', { email });
	} catch (error) {
		return error;
	}
}

async function reset_password(
	token: string,
	nova_senha: string
): Promise<boolean> {
	try {
		await ApiBack.post(`/reset_password?token=${token}`, {
			nova_senha: nova_senha,
		});
		return true;
	} catch (errro) {
		return false;
	}
}

export const AuthService = {
	login,
	logout,
	refresh,
	decrypt,
	getAccessToken,
	getRefreshToken,
	removeAccess,
	verify,
	forgot_password,
	reset_password,
	encrypt,
	loginWithMicrosoft,
	handleMicrosoftCallback,
	processMicrosoftCallback,
	processMicrosoftToken,
};
